import { FC, useMemo } from "react";
import { Box, Button, IconButton } from "@mui/material";

import RefreshIcon from "../../icons/RefreshIcon";
import CloseIcon from "../../icons/CloseIcon";

import Row from "../../ui/core/Row";
import CoreKeywordSearch from "../../ui/core/CoreKeywordSearch";
import CoreDropdown from "../../ui/core/CoreDropdown";
import { Payer } from "../../../services";
import { PreChatsHelper } from "../../helpers/preChatsHelper";
import { useChatCategoryStore } from "../../../hooks/useChatCategoryStore";
import { LOB_VALUE_TO_NAME } from "../../../config";
import { observer } from "mobx-react";

interface ContractResearchFiltersProps {
  payersData: Payer[];
  onFormChange: (field: any, value: string) => void;
}

export const ContractResearchFilters: FC<ContractResearchFiltersProps> =
  observer(({ payersData, onFormChange }) => {
    const {
      filters: formData,
      setContractResearchFilters,
      fetchContracts,
      columnFilters,
      clearAllColumnFilters,
    } = useChatCategoryStore();

    // Get available states (converted to state names)
    const availableStates = useMemo(
      () => PreChatsHelper.extractAvailableStates(payersData),
      [payersData],
    );

    // Get selected state data
    const selectedState = useMemo(() => {
      return payersData.find(
        (state) =>
          state.name === formData.state || state.code === formData.state,
      );
    }, [payersData, formData.state]);

    // Get payer options for selected state (display names for dropdown, but store API name as value)
    // If no state is selected, show all unique payers from all states
    const payersOptions = useMemo(
      () =>
        PreChatsHelper.extractPayerOptions(
          formData?.state,
          selectedState,
          payersData,
        ),
      [selectedState, formData.state, payersData],
    );

    // Get selected payer data from selected state
    const selectedPayer = useMemo(() => {
      if (!selectedState || !selectedState.payers || !formData.payer) {
        return null;
      }

      return selectedState.payers.find((p) => p.name === formData.payer);
    }, [selectedState, formData.payer]);

    // Get available LOB for selected payer in selected state (converted to display names)
    const availableLineOfBusiness = useMemo(() => {
      if (
        !selectedPayer ||
        !selectedPayer.lines_of_business ||
        selectedPayer.lines_of_business.length === 0
      ) {
        return [];
      }
      // Convert LOB values to display names
      return selectedPayer.lines_of_business.filter((name) => name).sort();
    }, [selectedPayer]);

    const onFilterChange = (filterName: string, value: string) => {
      setContractResearchFilters({ ...formData, [filterName]: value });
      fetchContracts();
    };

    const onDropdownChange = (field: string, value: string) => {
      onFormChange(field, value);
    };

    const onRefresh = () => {
      fetchContracts();
    };

    const hasFiltersApplied = !!(
      formData.contractSearchQuery ||
      formData.state ||
      formData.payer ||
      formData.lineOfBusiness ||
      Object.keys(columnFilters).length > 0
    );

    const onClearFilters = () => {
      setContractResearchFilters({
        ...formData,
        contractSearchQuery: "",
        state: "",
        payer: "",
        lineOfBusiness: "",
      });
      clearAllColumnFilters();
      fetchContracts();
    };

    return (
      <Row
        sx={{
          justifyContent: "space-between",
          padding: {
            xs: "10px",
            sm: "10px",
            md: "10px",
            lg: "10px",
            xl: "12px",
          },
          height: "auto",
          gap: 1,
          flexWrap: "nowrap",
          alignItems: "center",
          overflowX: "auto",
          overflowY: "hidden",
          width: "100%",
        }}
      >
        <IconButton
          aria-label="Refresh"
          onClick={onRefresh}
          sx={(theme) => ({
            border: "1px solid",
            borderRadius: "8px",
            width: 36,
            height: 36,
            flexShrink: 0,
            color: theme.palette.text.secondary,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          })}
        >
          <RefreshIcon />
        </IconButton>

        <Box sx={{ minWidth: "200px", maxWidth: "350px", width: "100%" }}>
          <CoreKeywordSearch
            showSearchIcon
            placeholder="Search contracts..."
            value={formData.contractSearchQuery || ""}
            onChange={(value) => onFilterChange("contractSearchQuery", value)}
          />
        </Box>

        {hasFiltersApplied ? (
          <Button
            variant="text"
            startIcon={<CloseIcon />}
            onClick={onClearFilters}
            sx={{ minWidth: "auto", flexShrink: 0, color: "error.dark" }}
          >
            Clear Filters
          </Button>
        ) : null}

        <Box sx={{ flex: 1 }} />

        <CoreDropdown
          label="State"
          disableOnNoOptions
          selected={formData?.state}
          options={availableStates}
          onChange={(val: string) => onDropdownChange("state", val)}
        />

        <CoreDropdown
          label="Plan Name"
          disableOnNoOptions
          selected={formData?.payer}
          options={payersOptions}
          onChange={(val: string) => onDropdownChange("payer", val)}
        />

        <CoreDropdown
          label="Line of Business"
          disableOnNoOptions
          selected={formData?.lineOfBusiness}
          options={availableLineOfBusiness?.map((value) => ({
            label: LOB_VALUE_TO_NAME[value] || value,
            value: value,
          }))}
          onChange={(val: string) => onDropdownChange("lineOfBusiness", val)}
        />
      </Row>
    );
  });
